import { useMemo, useState } from 'react'
import { computeResults, DEFAULT_STATE } from './calculator'
import type { CalculatorResults, CalculatorSetters, CalculatorState } from './types'

export function useCalculator(initialState: Partial<CalculatorState> = {}): {
  state: CalculatorState
  setters: CalculatorSetters
  results: CalculatorResults
} {
  const [state, setState] = useState<CalculatorState>(() => ({ ...DEFAULT_STATE, ...initialState }))

  const setters = useMemo<CalculatorSetters>(() => {
    const generic = (Object.keys(DEFAULT_STATE) as Array<keyof CalculatorState>).reduce(
      (acc, key) => ({
        ...acc,
        [key]: (value: number) => setState((prev) => ({ ...prev, [key]: value })),
      }),
      {} as CalculatorSetters
    )
    return {
      ...generic,
      age: (value: number) =>
        setState((prev) => {
          const newRetirementAge = Math.max(prev.retirementAge, value + 1)
          return {
            ...prev,
            age: value,
            retirementAge: newRetirementAge,
            lifeExpectancy: Math.max(prev.lifeExpectancy, newRetirementAge + 1),
          }
        }),
      retirementAge: (value: number) =>
        setState((prev) => {
          const newRetirementAge = Math.max(value, prev.age + 1)
          return {
            ...prev,
            retirementAge: newRetirementAge,
            lifeExpectancy: Math.max(prev.lifeExpectancy, newRetirementAge + 1),
          }
        }),
    }
  }, [])

  const results = useMemo(() => computeResults(state), [state])

  return { state, setters, results }
}
